import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  Typography,
  TablePagination
} from '@mui/material';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';

const DataTable = ({
  columns,
  data = [],
  loading = false,
  emptyTitle = 'No Data Available',
  emptySubtitle = 'There are no matching records to display yet.',
  premium = false
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const premiumStyles = {
    container: { border: 'none', background: 'transparent', },
    headCell: {
      fontWeight: 600,
      color: 'var(--subtitle-text)',
      textTransform: 'uppercase',
      fontSize: '0.75rem',
      letterSpacing: '0.05em',
      py: 2,
      borderBottom: '1px solid var(--border-light)'
    },
    row: { '&:hover': { backgroundColor: 'white' } },
    cell: { py: 2.5, borderBottom: '1px solid var(--border-light)', color: 'var(--header-title)' }
  };

  const originalStyles = {
    container: { borderRadius: 2, border: '1px solid #e2e8f0', mb: 3, overflow: 'hidden' },
    headCell: { fontWeight: 600, color: '#475569', textTransform: 'uppercase', fontSize: '0.8125rem' },
    row: { '&:hover': { backgroundColor: '#f1f5f9' } },
    cell: { color: 'inherit' }
  };

  const activeStyle = premium ? premiumStyles : originalStyles;

  return (
    <TableContainer component={Paper} elevation={0} sx={activeStyle.container}>
      <Table sx={{ minWidth: 650 }} aria-label="custom data table">
        <TableHead sx={{ backgroundColor: premium ? 'var(--background-page)' : '#f8fafc', borderBottom: premium ? '1px solid var(--border-light)' : 'none' }}>
          <TableRow>
            {columns.map((col) => (
              <TableCell
                key={col.id}
                align={col.align || 'left'}
                sx={{
                  ...activeStyle.headCell,
                  ...(col.width && { width: col.width })
                }}
              >
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 6 }}>
                <CircularProgress size={32} thickness={4} sx={{ color: premium ? 'var(--premium-dark)' : 'primary.main' }} />
              </TableCell>
            </TableRow>
          ) : data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 8 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', opacity: 0.7 }}>
                  <DashboardCustomizeOutlinedIcon sx={{ fontSize: 48, color: premium ? 'var(--border-light)' : '#94a3b8', mb: 2 }} />
                  <Typography variant="subtitle1" sx={{ color: '#64748b', fontWeight: 500 }}>
                    {emptyTitle}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                    {emptySubtitle}
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            paginatedData.map((row, index) => (
              <TableRow
                key={row.id || index}
                sx={{
                  '&:last-child td': { border: 0 },
                  transition: 'background-color 0.2s',
                  ...activeStyle.row
                }}
              >
                {columns.map((col) => (
                  <TableCell
                    key={col.id}
                    align={col.align || 'left'}
                    sx={activeStyle.cell}
                  >
                    {col.render ? col.render(row) : row[col.id]}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {!loading && data.length > 0 && (
        <Box sx={premium ? {
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          py: 2,
          borderTop: '1px solid var(--border-light)',
          bgcolor: 'white'
        } : { borderTop: '1px solid #e2e8f0' }}>
          {premium && (
            <Typography variant="caption" sx={{ color: 'var(--subtitle-text)', fontWeight: 500 }}>
              Page {page + 1} of {Math.ceil(data.length / rowsPerPage)}
            </Typography>
          )}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={data.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              border: 'none',
              '& .MuiTablePagination-toolbar': { p: premium ? 0 : 2 },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                color: premium ? 'var(--subtitle-text)' : 'inherit',
                fontSize: premium ? '0.8rem' : 'inherit',
                fontWeight: premium ? 500 : 'inherit'
              }
            }}
            labelRowsPerPage={premium ? 'SHOW:' : 'Rows per page:'}
          />
        </Box>
      )}
    </TableContainer>
  );
};

export default DataTable;
